import { ApolloCache, FieldPolicy, Reference } from '@apollo/client';
import { AdminCreatorsPageQueryVariables } from 'generated/graphql';

type CachedPage = {
  __typename?: string;
  items: Reference[];
  filteredCount: number;
  totalCount: number;
};

export const adminCreatorsPageFieldPolicy: FieldPolicy<CachedPage> = {
  keyArgs: ['filter', 'sort'],
  merge(existing, incoming, { args }) {
    const offset = typeof args?.offset === 'number' ? args.offset : 0;
    if (!existing || offset === 0) return incoming;

    const items = existing.items.slice(0, offset);
    items.splice(offset, 0, ...incoming.items);

    return { ...incoming, items };
  },
};

const creatorCacheId = <T>(cache: ApolloCache<T>, creatorId: number) =>
  cache.identify({ __typename: 'Creator', id: creatorId });

export const removeCreatorFromCache = <T>(
  cache: ApolloCache<T>,
  creatorId: number,
): void => {
  cache.modify({
    fields: {
      adminCreatorsPage(existing, { readField }) {
        const page: CachedPage = existing;
        const items = page.items.filter(
          (item) => readField('id', item) !== creatorId,
        );
        const removed = page.items.length - items.length;
        return {
          ...page,
          items,
          filteredCount: Math.max(0, page.filteredCount - removed),
          totalCount: Math.max(0, page.totalCount - 1),
        };
      },
    },
  });
  cache.evict({ id: creatorCacheId(cache, creatorId) });
  cache.gc();
};

// A tier change can move the row out of a tier-filtered list, so that list is
// evicted and its watching query fetches it again from offset 0.
export const writeCreatorTier = <T>(
  cache: ApolloCache<T>,
  p: { influencerId: number; tier: number },
  viewedList: AdminCreatorsPageQueryVariables,
): void => {
  cache.modify({
    id: creatorCacheId(cache, p.influencerId),
    fields: { tier: () => p.tier },
  });
  if (viewedList.filter?.rules?.some((rule) => rule.field === 'tier')) {
    cache.evict({ fieldName: 'adminCreatorsPage', args: viewedList });
  }
};
